<template>
    <div class="border-border p-6 rounded-xl flex flex-col justify-between" :class="[isEditingMode ? 'border-dashed border-4 bg-surface-sunken' : 'border-2 bg-surface-raised']">
        <div class="mb-3 md:mb-6 flex gap-3 md:gap-6 items-start">
            <slot name="icon"></slot>
            <h3 class="title-subsection grow">{{ questionText }}</h3>
            <BaseTooltip v-if="evidenceMessages.length > 0" :as-child="false" trigger-aria-label="How Nadia learned this">
                <template #trigger>
                    <BaseButton theme="neutral" icon-only class="hidden md:inline-flex" aria-label="Learned from conversations">
                        <i class="bi bi-question-circle" aria-hidden="true"></i>
                    </BaseButton>
                </template>
                <template #content>
                    <div class="max-w-80">{{ tooltipText }}</div>
                </template>
            </BaseTooltip>
        </div>
        <div class="rounded-xl px-6 py-3 focus-field-within" :class="[isEditingMode ? 'bg-control-bg border-2 border-control-border' : 'bg-surface-sunken']">
            <BaseTextarea
                v-model:input="input"
                class="w-full my-2"
                :placeholder="placeholder"
                :disabled="!isEditingMode"
                :aria-label="`Textarea to answer: ${questionText}`"
                @keydown.enter="handleSave" />
            <div class="flex gap-4 justify-between items-center">
                <BaseTooltip v-if="evidenceMessages.length > 0" :as-child="false" trigger-aria-label="How Nadia learned this">
                    <template #trigger>
                        <BaseButton theme="neutral" icon-only class="md:hidden" aria-label="Learned from conversations">
                            <i class="bi bi-question-circle" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content>
                        <div class="max-w-80">{{ tooltipText }}</div>
                    </template>
                </BaseTooltip>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss class="py-2" />
                <div class="flex gap-3 ml-auto items-center">
                    <LoadingSpinner v-if="isCreateFetching || isUpdateFetching || isDeleteFetching" class="w-6 h-6" />
                    <template v-else-if="isEditingMode">
                        <BaseButton
                            v-if="!isTranscribing"
                            :theme="isRecording ? 'primary' : 'tertiary'"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="isRecording ? 'Stop recording' : 'Start recording'"
                            :aria-pressed="isRecording"
                            :title="isRecording ? 'Stop recording' : 'Start recording'"
                            @click="handleRecord">
                            <i class="bi" :class="isRecording ? 'bi-stop-circle' : 'bi-mic'" aria-hidden="true"></i>
                        </BaseButton>
                        <LoadingSpinner v-else class="w-6 h-6" />
                        <BaseButton theme="secondary" :disabled="!input || isDisabled" @click="handleSave"> Save </BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="`Edit Profile Answer for ${questionText}`"
                            title="Edit Profile Answer"
                            @click="handleEdit">
                            <i class="bi bi-pencil text-xl/none" aria-hidden="true"></i>
                        </BaseButton>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="`Delete Profile Answer for ${questionText}`"
                            title="Delete Profile Answer"
                            @click="isConfirmDeleteDialogOpen = true">
                            <i class="bi bi-trash text-xl/none" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                </div>
            </div>
        </div>
        <ConfirmationDialog
            v-model:open="isConfirmDeleteDialogOpen"
            title="Delete answer?"
            text="Are you sure you want to delete this answer? This action cannot be undone."
            confirm-label="Delete"
            @confirm="deleteExecute" />
    </div>
</template>

<script setup>
import { transcribeRecording } from "/js/transcription.js";
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useMicConsent } from "~vue/composables/useMicConsent";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { isImeComposing } from "~vue/utils/keyboard";
import { logError } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

const { question, recorder, isEditing } = defineProps({
    question: { type: Object, required: true },
    placeholder: { type: String, default: "Share your thoughts — just start typing or record a quick voice note" },
    isEditing: { type: Boolean, default: false }, // used to set initial state of editing mode
    isDisabled: { type: Boolean, default: false },
    recorder: { type: Object, required: true },
});
const emit = defineEmits(["edit", "permission-open", "permission-close", "record-start", "record-end"]);

const isEditingMode = ref(isEditing);

const questionText = computed(() => (isEditing ? question.question : (question.heading ?? question.key_display)));
const evidenceMessages = computed(() => question.evidence_messages.map(({ message }) => message).join(", "));
const tooltipText = computed(() => `Learned from: ${evidenceMessages.value}`);

const input = ref(question.answer);
const error = ref(null);
const isConfirmDeleteDialogOpen = ref(false);

watch(input, () => {
    if (error.value) {
        error.value = null;
    }
});

const isRecording = ref(false);
const isTranscribing = ref(false);

watch(isRecording, (newValue) => {
    if (!newValue) {
        emit("record-end");
    }
});

const { micConsentBlocks } = useMicConsent();

const handleRecord = async () => {
    error.value = null;
    if (isRecording.value) {
        isRecording.value = false;
        recorder.stop();
    } else {
        try {
            if (recorder.isRecording) {
                return;
            }
            if (micConsentBlocks(handleRecord)) {
                return;
            }
            isRecording.value = true;
            recorder.on("finish", handleTranscribe);
            await recorder.start(async (micPermissionState) => {
                if (micPermissionState === "prompt") {
                    emit("permission-open");
                }
            });
            emit("record-start", { questionKey: question.key });
        } catch (e) {
            logError(`Failed to start recording: ${e}`);
            recorder.off("finish", handleTranscribe);
            isRecording.value = false;
            if (e.name === "NotAllowedError") {
                error.value = "Please allow microphone access to record your answer.";
            } else {
                error.value = "Failed to start recording. Please try again.";
            }
        } finally {
            emit("permission-close");
        }
    }
};

const onError = (e) => {
    logError(`Failed to save answer: ${e}`);
    error.value = "Failed to save answer. Please try again.";
};
const createPayload = ref({});
const {
    onFetchError: onCreateError,
    onFetchResponse: onCreateResponse,
    execute: createExecute,
    isFetching: isCreateFetching,
} = useFetch("/accounts/profile-data/", { immediate: false }).post(createPayload);
onCreateError((e) => {
    onError(e);
});
onCreateResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
});

const updatePayload = ref({});
const {
    onFetchError: onUpdateError,
    onFetchResponse: onUpdateResponse,
    execute: updateExecute,
    isFetching: isUpdateFetching,
} = useFetch(`/accounts/profile-data/${+question.id}`, { immediate: false }).put(updatePayload);
onUpdateError((e) => {
    onError(e);
});
onUpdateResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
    if (!isEditing) {
        isEditingMode.value = false;
    }
});

const {
    onFetchError: onDeleteError,
    onFetchResponse: onDeleteResponse,
    execute: deleteExecute,
    isFetching: isDeleteFetching,
} = useFetch(`/accounts/profile-data/${+question.id}`, { immediate: false }).delete();
onDeleteError((e) => {
    onError(e);
});
onDeleteResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
});

const handleSave = (e) => {
    if (e instanceof KeyboardEvent && isImeComposing(e)) {
        return;
    }
    if (!e.shiftKey) {
        e.preventDefault();
        if (isEditingMode.value && !!question.id) {
            updatePayload.value = { value: input.value, profile_question_key: question.key_display };
            updateExecute();
        } else {
            createPayload.value = { profile_question_key: question.key_display, value: input.value };
            createExecute();
        }
    }
};

const handleEdit = () => {
    isEditingMode.value = true;
};

const handleTranscribe = async ({ blob }) => {
    isTranscribing.value = true;
    error.value = null;
    try {
        const transcript = await transcribeRecording({
            blob,
            onStart: () => {
                isTranscribing.value = true;
            },
            onError: (err) => {
                logError(err, {
                    context: { transcription: { source: "ProfileQuestionAnswer" } },
                    tags: { error_type: "transcription", error_code: err.error_code },
                });
            },
        });
        isTranscribing.value = false;
        input.value = transcript;
    } catch (e) {
        logError(e, {
            context: { transcription: { source: "ProfileQuestionAnswer" } },
            tags: { error_type: "transcription" },
        });
        isTranscribing.value = false;
        error.value = "Transcription failed. Please try again.";
    } finally {
        recorder.off("finish", handleTranscribe);
        isRecording.value = false;
    }
};
</script>
